<script lang="ts">
    import { goto } from "$app/navigation";
    import { page } from "$app/stores";

    import BookmarksIcon from "$lib/assets/BookmarksIcon.svg";
    import PuzzlePieceWhite from "$lib/assets/PuzzlePieceWhite.svg";
    import Bug from "$lib/assets/Bug.svg";
    import HandWavingWhite from "$lib/assets/HandWavingWhite.svg";
    import Gear from "$lib/assets/Gear.svg";
    import LogoTitle from "$lib/assets/LogoTitle.png";
    import PencilWhite from "$lib/assets/PencilWhite.svg";
    import HowItWorks from "$lib/assets/HowItWorks.svg";
    import PowerButton from "$lib/assets/PowerButton.svg";
    import ProfilePictureDefault from "$lib/assets/ProfilePictureDefault.svg";
    import SearchNav from "$lib/assets/SearchNav.svg";

    import ProfilePicture from "$lib/components/ProfilePicture.svelte";
    import { reportBug } from "$lib/functions/reportBug.js";

    import type { ModalSettings, PopupSettings } from "@skeletonlabs/skeleton";
    import { getDrawerStore, getModalStore, ListBox, ListBoxItem, popup } from "@skeletonlabs/skeleton";

    export let data;
    let { session, supabase } = data;
    $: ({ session, supabase } = data);

    const drawerStore = getDrawerStore();
    const modalStore = getModalStore();

    function signIn(): void {
        const modal: ModalSettings = {
            type: "component",
            component: "signIn",
            meta: {
                data: data,
            },
        };
        modalStore.trigger(modal);
    }

    let comboboxValue: string;

    const popupCombobox: PopupSettings = {
        event: "click",
        target: "popupCombobox",
        placement: "top",
        closeQuery: ".listbox-item",
    };

    function drawerClose(): void {
        drawerStore.close();
    }

    function bookACall(): void {
        const modal: ModalSettings = {
            type: "component",
            component: "bookACall",
        };
        modalStore.trigger(modal);
    }

    function noPermission(msg: string): boolean {
        const modal: ModalSettings = {
            type: "component",
            component: "error",
            body: msg,
        };
        modalStore.trigger(modal);
        return false;
    }

    async function handleWorkspaceClick() {
        let permission = true;
        let errorMsg =
            "Access Denied: You do not have permission to view the Workspace. If you believe this is a mistake, please contact the site administrator.";

        // Check if user is admin
        const { count: adminCount, error: adminError } = await supabase
            .from("admins")
            .select("*", { head: true, count: "exact" })
            .eq("email", session.user.email);

        if (adminError) {
            permission = noPermission(errorMsg);
        }

        if (adminCount == 0) {
            // Check if the company that the user is in is subscribed
            const { data: userData, error: userError } = await supabase
                .from("users")
                .select("company_id")
                .eq("id", session.user.id)
                .single();

            if (userError) {
                permission = noPermission(errorMsg);
            } else if (userData) {
                const { data: companyData, error: companyError } = await supabase
                    .from("company_data")
                    .select("subscribed")
                    .eq("id", userData.company_id)
                    .single();

                if (companyError) {
                    permission = noPermission(errorMsg);
                } else if (!companyData || !companyData.subscribed) {
                    permission = noPermission(errorMsg);
                }
            } else {
                permission = noPermission(errorMsg);
            }
        }

        if (permission) {
            drawerClose();
            window.location.href = "/workspace/overview";
        }
    }
</script>

<div class="flex flex-col h-full p-2.5 xs:pt-14">
    <header>
        <a href="/search" class="xs:hidden" on:click={drawerClose}>
            <img class="px-4 py-6" src={LogoTitle} />
        </a>
    </header>

    <nav class="list-nav flex flex-grow">
        <ul class="flex flex-col flex-grow font-semibold">
            <li class="rounded-3xl hover:bg-gray-600" class:bg-gray-600={$page.url.pathname.includes("scoutcapture")}>
                {#if !session}
                    <a on:click={signIn}>
                        <img class="badge-icon rounded-none" src={PuzzlePieceWhite} />
                        <span>Find My Match</span>
                    </a>
                {:else}
                    <a href="/scoutcapture" on:click={drawerClose}>
                        <img class="badge-icon rounded-none" src={PuzzlePieceWhite} />
                        <span>Find My Match</span>
                    </a>
                {/if}
            </li>
            <li class="rounded-3xl hover:bg-gray-600" class:bg-gray-600={$page.url.pathname.includes("search")}>
                <a href="/search" on:click={drawerClose}>
                    <img class="badge-icon rounded-none" src={SearchNav} />
                    <span>Search Grants</span>
                </a>
            </li>
            <li class="rounded-3xl hover:bg-gray-600" class:bg-gray-600={$page.url.pathname.includes("saved-grants")}>
                {#if !session}
                    <a on:click={signIn}>
                        <img class="badge-icon rounded-none" src={BookmarksIcon} />
                        <span>Saved Grants</span>
                    </a>
                {:else}
                    <a href="/saved-grants" on:click={drawerClose}>
                        <img class="badge-icon rounded-none" src={BookmarksIcon} />
                        <span>Saved Grants</span>
                    </a>
                {/if}
            </li>
            <li class="rounded-3xl hover:bg-gray-600" class:bg-gray-600={$page.url.pathname.includes("drafts")}>
                {#if !session}
                    <a on:click={signIn}>
                        <img class="badge-icon rounded-none" src={PencilWhite} />
                        <span>Workspace</span>
                    </a>
                {:else}
                    <a on:click={handleWorkspaceClick}>
                        <img class="badge-icon rounded-none" src={PencilWhite} />
                        <span>Workspace</span>
                    </a>
                {/if}
            </li>
            <li class="rounded-3xl hover:bg-gray-600" class:bg-gray-600={$page.url.pathname.includes("how-it-works")}>
                <a href="/how-it-works" on:click={drawerClose}>
                    <img class="badge-icon rounded-none" src={HowItWorks} />
                    <span>How It Works</span>
                </a>
            </li>
            <li class="flex-grow"></li>
            <li class="rounded-3xl hover:bg-gray-600">
                <a type="button" on:click={bookACall}>
                    <img class="badge-icon rounded-none" src={HandWavingWhite} />
                    <span>Book a Call</span>
                </a>
            </li>
            <li class="rounded-3xl hover:bg-gray-600">
                <a type="button" on:click={reportBug(data.user?.id, data.user?.email, data.user?.full_name)}>
                    <img class="badge-icon rounded-none" src={Bug} />
                    <span>Report a Bug</span>
                </a>
            </li>
            <li class="rounded-3xl hover:bg-gray-600" class:bg-gray-600={$page.url.pathname.includes("account")}>
                {#if !session}
                    <a on:click={signIn}>
                        <img class="badge-icon" src={ProfilePictureDefault} />
                        <span>Log In</span>
                    </a>
                {:else}
                    <a use:popup={popupCombobox} class="w-48">
                        <ProfilePicture user_id={session.user.id} user_name={data.user?.full_name ?? ""} />
                        <!-- <img class="badge-icon" src={ProfilePictureDefault} /> -->
                        <span class="truncate">{data.user?.full_name || "Account Settings"}</span>
                    </a>
                    <div class="card w-48" data-popup="popupCombobox">
                        <ListBox rounded="rounded-lg">
                            <ListBoxItem
                                class="variant-filled-surface hover:bg-secondary-600"
                                bind:group={comboboxValue}
                                on:click={() => {
                                    drawerClose();
                                    goto("/account");
                                }}
                            >
                                <svelte:fragment slot="lead">
                                    <img class="badge-icon" src={Gear} />
                                </svelte:fragment>
                                <span>Account Settings</span>
                            </ListBoxItem>

                            <hr class="block bg-[#A0AEC0] h-[1.5px] ml-[5%] mr-[5%]" />

                            <ListBoxItem
                                class="variant-filled-surface hover:bg-secondary-600"
                                bind:group={comboboxValue}
                                on:click={async () => {
                                    drawerClose();
                                    await supabase.auth.signOut();
                                    goto("/search", { invalidateAll: true });
                                }}
                            >
                                <svelte:fragment slot="lead">
                                    <img class="badge-icon" src={PowerButton} />
                                </svelte:fragment>
                                <span>Sign Out</span>
                            </ListBoxItem>
                        </ListBox>
                        <div class="arrow bg-surface-100-800-token"></div>
                    </div>
                {/if}
            </li>
        </ul>
    </nav>
</div>
